import React, { useContext, useReducer, useState, useEffect, useCallback, useMemo } from 'react';
import { styleReducer } from './styleReducer';
import { StyleContext, styleInitialState, ISelectedGroup, TTypeOfModalWindow } from './styleContext';
import { StyleActionsTypes } from './styleActions';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	IEditRestaurantContext,
	EditRestaurantContext,
} from '../../../../../editRestaurantState/editRestaurantContext';
import {
	TLanguageIDs,
	TFileId,
	IGetRestaurantStylesInfo,
	IStyleSettings,
	TMediaFileType,
	IMediaSettings,
	IMediaSettingsForUpdate,
	IGroupsOfMediaSettingsForUpdate,
	IItemsOfGroupsOfMediaSettingsForUpdate,
	ICustomMediaSettingsForUpdate,
	ICustomGroups,
	ICustomItemsOfGroups,
	ICustomDocumentSettingsForUpdate,
	IDocumentSettings,
	IDocumentSettingsForUpdate,
	TKeyForNewItemToCarousel,
	TAllergensShowCondition,
} from 'types';
import { NON_EXISTENT_KEY, ALLERGEN_SETTING_KEY, TERMS_SETTING_KEY } from 'appConstants';
import existingMediaSettingsKeys from 'public/mediaSettings.json';
import { getRestaurantStylesInfo, updateRestaurantStyles } from 'backend/restaurants';
import { useFetchData } from 'hooks/useFetchData';

const mapGroupItemsForNormalDisplay = ({
	items,
	translationId,
	mediaFileType,
	sourceUrl,
	fileId,
}: {
	items: ICustomItemsOfGroups[];
	translationId: string;
	mediaFileType: TMediaFileType;
	sourceUrl: string;
	fileId: TFileId;
}): ICustomItemsOfGroups[] =>
	items.map((item) => {
		if (item.translationId !== translationId) {
			return item;
		}
		return {
			id: item.id,
			translationId: item.translationId,
			key: item.key,
			mediaFileType,
			filePath: item.filePath,
			sourceUrl,
			fileId,
		};
	});

const mapGroupItemsClearingCarouselItem = ({
	items,
	translationId,
}: {
	items: ICustomItemsOfGroups[];
	translationId: string;
}): ICustomItemsOfGroups[] =>
	items.map((item) => {
		if (item.translationId !== translationId) {
			return item;
		}
		return {
			id: item.id,
			translationId: item.translationId,
			key: item.key,
			mediaFileType: item.mediaFileType,
			filePath: item.filePath,
			sourceUrl: '',
			fileId: null,
		};
	});

const mapGroupItemsReplacingPdf = ({
	items,
	translationIdOfItemForDeletion,
	translationId,
	itemKey,
	fileId,
}: {
	items: ICustomItemsOfGroups[];
	translationIdOfItemForDeletion: string | undefined;
	translationId: string;
	itemKey: TKeyForNewItemToCarousel;
	fileId: TFileId;
}): ICustomItemsOfGroups[] => {
	const clearedItems = items.map((item) => {
		if (item.translationId !== translationIdOfItemForDeletion) {
			return item;
		}
		return {
			id: item.id,
			translationId: item.translationId,
			key: item.key,
			mediaFileType: item.mediaFileType,
			filePath: item.filePath,
			sourceUrl: '',
			fileId: null,
		};
	});

	return [
		...clearedItems,
		{
			id: 'newItem',
			translationId,
			key: itemKey,
			mediaFileType: 3 as TMediaFileType,
			filePath: '',
			sourceUrl: '',
			fileId,
		},
	];
};

const filterAndMapGroupItemsForSubmit = (items: ICustomItemsOfGroups[]): IItemsOfGroupsOfMediaSettingsForUpdate[] => {
	const filteredItems = items.filter((item) => item.id !== 'newItem' || item.fileId !== null);

	return filteredItems.map((item) => {
		if (item.id !== 'newItem') {
			return {
				id: item.id,
				translationId: item.translationId,
				key: item.key,
				mediaFileType: item.mediaFileType,
				fileId: item.fileId,
			};
		}
		return {
			id: null,
			translationId: null,
			key: item.key,
			mediaFileType: item.mediaFileType,
			fileId: item.fileId,
		};
	});
};

const StyleState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(styleReducer, styleInitialState);
	const { showLoader, hideLoader, featureFlags } = useContext<IAppContext>(AppContext);
	const { visibleForm, IDSelectedCustomer } = useContext<IEditRestaurantContext>(EditRestaurantContext);
	const [isFirstEntry, setIsFirstEntry] = useState(true);

	const params = new URL(document.location.href).searchParams;
	const restaurantID = params.get('restaurantID');
	const needStyleDataRequest = IDSelectedCustomer && restaurantID && visibleForm === 'styleForm';

	const { response, refetch, loading } = useFetchData<IGetRestaurantStylesInfo, string>(
		needStyleDataRequest ? getRestaurantStylesInfo : null,
		IDSelectedCustomer || '',
		restaurantID || '',
	);

	const getMediaSettingsChanged = (mediaSettings: IMediaSettings[]) => {
		const updatedMediaSettingsChanged = mediaSettings.map<ICustomMediaSettingsForUpdate>((mediaSetting) => {
			const filteredMediaSettingGroups = mediaSetting.groups.filter((group) => {
				if (group.key === 'fiscal_receipt_dialog_background') {
					return featureFlags.HAS_FISCAL_RECEIPT_DIALOG_BACKGROUND;
				}
				return true;
			});

			const updatedGroups = filteredMediaSettingGroups.map<ICustomGroups>((group) => {
				const updatedItems = group.items.map<ICustomItemsOfGroups>((item) => {
					return {
						id: item.id,
						translationId: item.translationId,
						key: item.key,
						mediaFileType: item.mediaFileType,
						filePath: item.filePath,
						sourceUrl: '',
						fileId: '',
					};
				});
				return {
					id: group.id,
					key: group.key,
					isMultipleItems: group.isMultipleItems,
					items: updatedItems,
				};
			});
			return {
				languageId: mediaSetting.languageId,
				groups: updatedGroups,
			};
		});
		return updatedMediaSettingsChanged;
	};

	const getDocumentSettingsChanged = (documentSettings: IDocumentSettings[]) => {
		const filteredDocumentSettings = documentSettings.filter((el) =>
			Object.keys(existingMediaSettingsKeys).includes(el.key || NON_EXISTENT_KEY),
		);

		const updatedDocumentSettingsChanged = filteredDocumentSettings.map<ICustomDocumentSettingsForUpdate>(
			(documentSetting) => {
				return {
					...documentSetting,
					allergensShowCondition:
						documentSetting.key === ALLERGEN_SETTING_KEY && documentSetting.allergensShowCondition === null
							? 'GoingToMenu'
							: documentSetting.allergensShowCondition,
					termsShowInModal:
						documentSetting.key === TERMS_SETTING_KEY && documentSetting.termsShowInModal === null
							? false
							: documentSetting.termsShowInModal,
					fileId: null,
					fileName: null,
				};
			},
		);
		return updatedDocumentSettingsChanged;
	};

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (isFirstEntry) {
			refetch();
		}
	}, [IDSelectedCustomer, restaurantID, visibleForm]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: StyleActionsTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		if (response) {
			const mediaSettingsForUpdate = getMediaSettingsChanged(response.data.mediaSettings);
			const documentSettingsForUpdate = getDocumentSettingsChanged(response.data.documentSettings);
			dispatch({
				type: StyleActionsTypes.SET_ALL_DATA,
				payload: {
					styleSettingColors: response.data.styleSettingColors,
					mediaSettingsForUpdate: mediaSettingsForUpdate,
					documentSettingsForUpdate: documentSettingsForUpdate,
					useCustomerSettings: response.data.useCustomerSettings,
					additionalSetting: response.data.additionalSetting,
				},
			});
			setIsFirstEntry(false);
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: StyleActionsTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const setStyleSettings = useCallback(
		(styleItemId: string, value: string, key: string) => {
			if (state.styleSettings.some((el) => el.id === styleItemId)) {
				const updatedStyleSettings = state.styleSettings.map<IStyleSettings>((el) => {
					if (el.id === styleItemId) {
						return {
							id: el.id,
							key: el.key,
							value: value,
						};
					}
					return el;
				});
				dispatch({
					type: StyleActionsTypes.SET_STYLE_SETTINGS,
					payload: {
						styleSettings: updatedStyleSettings,
					},
				});
			} else {
				dispatch({
					type: StyleActionsTypes.SET_STYLE_SETTINGS,
					payload: {
						styleSettings: [
							...state.styleSettings,
							{
								id: styleItemId,
								key: key,
								value: value,
							},
						],
					},
				});
			}
		},
		[state.styleSettings],
	);

	const setMediaSettingsFromNormalDisplay = useCallback(
		(
			languageId: null | TLanguageIDs,
			groupId: string,
			translationId: string,
			mediaFileType: TMediaFileType,
			sourceUrl: string,
			fileId: TFileId,
		) => {
			const updatedMediaSettingsForUpdate = state.mediaSettingsForUpdate.map((language) => {
				if (language.languageId !== languageId) {
					return language;
				}
				const updatedGroups = language.groups.map((group) => {
					if (group.id !== groupId) {
						return group;
					}
					const updatedItems = mapGroupItemsForNormalDisplay({
						items: group.items,
						translationId,
						mediaFileType,
						sourceUrl,
						fileId,
					});
					return {
						id: group.id,
						key: group.key,
						isMultipleItems: group.isMultipleItems,
						items: updatedItems,
					};
				});
				return {
					languageId: language.languageId,
					groups: updatedGroups,
				};
			});
			dispatch({
				type: StyleActionsTypes.SET_MEDIA_SETTINGS,
				payload: {
					mediaSettingsForUpdate: updatedMediaSettingsForUpdate,
				},
			});
		},
		[state.mediaSettingsForUpdate],
	);

	const addMediaSettingsToCarousel = useCallback(
		(
			languageId: null | TLanguageIDs,
			groupId: string,
			translationId: string,
			itemKey: TKeyForNewItemToCarousel,
			mediaFileType: TMediaFileType,
			sourceUrl: string,
			fileId: TFileId,
		) => {
			const updatedMediaSettingsForUpdate = state.mediaSettingsForUpdate.map((language) => {
				if (language.languageId !== languageId) {
					return language;
				}
				const updatedGroups = language.groups.map((group) => {
					if (group.id !== groupId) {
						return group;
					}
					return {
						id: group.id,
						key: group.key,
						isMultipleItems: group.isMultipleItems,
						items: [
							...group.items,
							{
								id: 'newItem',
								translationId: translationId,
								key: itemKey,
								mediaFileType: mediaFileType,
								filePath: '',
								sourceUrl: sourceUrl,
								fileId: fileId,
							},
						],
					};
				});
				return {
					languageId: language.languageId,
					groups: updatedGroups,
				};
			});
			dispatch({
				type: StyleActionsTypes.SET_MEDIA_SETTINGS,
				payload: {
					mediaSettingsForUpdate: updatedMediaSettingsForUpdate,
				},
			});
		},
		[state.mediaSettingsForUpdate],
	);

	const deleteMediaSettingsToCarousel = useCallback(
		(languageId: TLanguageIDs, groupId: string, translationId: string) => {
			const updatedMediaSettingsForUpdate = state.mediaSettingsForUpdate.map((language) => {
				if (language.languageId !== languageId) {
					return language;
				}
				const updatedGroups = language.groups.map((group) => {
					if (group.id !== groupId) {
						return group;
					}
					const updatedItems = mapGroupItemsClearingCarouselItem({
						items: group.items,
						translationId,
					});
					return {
						id: group.id,
						key: group.key,
						isMultipleItems: group.isMultipleItems,
						items: updatedItems,
					};
				});
				return {
					languageId: language.languageId,
					groups: updatedGroups,
				};
			});
			dispatch({
				type: StyleActionsTypes.SET_MEDIA_SETTINGS,
				payload: {
					mediaSettingsForUpdate: updatedMediaSettingsForUpdate,
				},
			});
		},
		[state.mediaSettingsForUpdate],
	);

	const replacePdfToCarousel = useCallback(
		({
			translationIdOfItemForDeletion,
			languageId,
			groupId,
			translationId,
			itemKey,
			fileId,
		}: {
			translationIdOfItemForDeletion: string | undefined;
			languageId: TLanguageIDs;
			groupId: string;
			translationId: string;
			itemKey: TKeyForNewItemToCarousel;
			fileId: TFileId;
		}) => {
			const updatedMediaSettingsForUpdate = state.mediaSettingsForUpdate.map((language) => {
				if (language.languageId !== languageId) {
					return language;
				}
				const updatedGroups = language.groups.map((group) => {
					if (group.id !== groupId) {
						return group;
					}
					const updatedItems = mapGroupItemsReplacingPdf({
						items: group.items,
						translationIdOfItemForDeletion,
						translationId,
						itemKey,
						fileId,
					});
					return {
						id: group.id,
						key: group.key,
						isMultipleItems: group.isMultipleItems,
						items: updatedItems,
					};
				});
				return {
					languageId: language.languageId,
					groups: updatedGroups,
				};
			});
			dispatch({
				type: StyleActionsTypes.SET_MEDIA_SETTINGS,
				payload: {
					mediaSettingsForUpdate: updatedMediaSettingsForUpdate,
				},
			});
		},
		[state.mediaSettingsForUpdate],
	);

	const toggleUseCustomerSettings = useCallback(() => {
		dispatch({ type: StyleActionsTypes.TOGGLE_USE_CUSTOMER_SETTINGS });
	}, []);

	const setFileIdForAdditionalSetting = useCallback((value: string) => {
		dispatch({
			type: StyleActionsTypes.SET_FILE_ID_FOR_ADDITIONAL_SETTING,
			payload: {
				fileIdForAdditionalSetting: value,
			},
		});
	}, []);

	const setGroup = useCallback((value: ISelectedGroup) => {
		dispatch({
			type: StyleActionsTypes.SET_GROUP,
			payload: {
				group: value,
			},
		});
	}, []);

	const setLanguage = useCallback((value: TLanguageIDs) => {
		dispatch({
			type: StyleActionsTypes.SET_LANGUAGE,
			payload: {
				language: value,
			},
		});
	}, []);

	const addGroupForLoader = useCallback(
		(groupId: string) => {
			const updatedLoaders = [...state.groupsForLoader, groupId];
			dispatch({
				type: StyleActionsTypes.ADD_GROUP_FOR_LOADER,
				payload: {
					updatedLoaders,
				},
			});
		},
		[state.groupsForLoader],
	);

	const removeGroupForLoader = useCallback(
		(groupId: string) => {
			const updatedLoaders = state.groupsForLoader.filter((el) => el !== groupId);
			dispatch({
				type: StyleActionsTypes.REMOVE_GROUP_FOR_LOADER,
				payload: {
					updatedLoaders,
				},
			});
		},
		[state.groupsForLoader],
	);

	const addFileIdForDocumentSettingsForUpdate = useCallback(
		({
			documentSettingId,
			fileId,
			fileName,
			filePath,
		}: {
			documentSettingId: string;
			fileId: TFileId;
			fileName: string;
			filePath: Nullable<string>;
		}) => {
			const updatedDocumentSettingsForUpdate =
				state.documentSettingsForUpdate.map<ICustomDocumentSettingsForUpdate>((el) => {
					if (el.id === documentSettingId) {
						return {
							...el,
							fileId,
							fileName,
							filePath,
						};
					}
					return el;
				});

			dispatch({
				type: StyleActionsTypes.CHANGE_DOCUMENT_SETTINGS_FOR_UPDATE,
				payload: {
					documentSettingsForUpdate: updatedDocumentSettingsForUpdate,
				},
			});
		},
		[state.documentSettingsForUpdate],
	);

	const setAllergenOptions = useCallback(
		({
			documentSettingId,
			allergensShowCondition,
		}: {
			documentSettingId: string;
			allergensShowCondition: TAllergensShowCondition;
		}) => {
			const updatedDocumentSettingsForUpdate =
				state.documentSettingsForUpdate.map<ICustomDocumentSettingsForUpdate>((el) => {
					if (el.id === documentSettingId) {
						return {
							...el,
							allergensShowCondition,
						};
					}
					return el;
				});

			dispatch({
				type: StyleActionsTypes.CHANGE_DOCUMENT_SETTINGS_FOR_UPDATE,
				payload: {
					documentSettingsForUpdate: updatedDocumentSettingsForUpdate,
				},
			});
		},
		[state.documentSettingsForUpdate],
	);

	const toggleShowModal = useCallback(
		(documentSettingId: string) => {
			const updatedDocumentSettingsForUpdate =
				state.documentSettingsForUpdate.map<ICustomDocumentSettingsForUpdate>((el) => {
					if (el.id === documentSettingId) {
						return {
							...el,
							termsShowInModal: !el.termsShowInModal,
						};
					}
					return el;
				});

			dispatch({
				type: StyleActionsTypes.CHANGE_DOCUMENT_SETTINGS_FOR_UPDATE,
				payload: {
					documentSettingsForUpdate: updatedDocumentSettingsForUpdate,
				},
			});
		},
		[state.documentSettingsForUpdate],
	);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: StyleActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const submit = useCallback(async () => {
		const updatedMediaSettingsForUpdate = state.mediaSettingsForUpdate.map<IMediaSettingsForUpdate>((language) => {
			const updatedGroups = language.groups.map<IGroupsOfMediaSettingsForUpdate>((group) => {
				const updatedItems = filterAndMapGroupItemsForSubmit(group.items);
				return {
					id: group.id,
					key: group.key,
					items: updatedItems,
				};
			});
			return {
				languageId: language.languageId,
				groups: updatedGroups,
			};
		});

		const finalFileId = (fileId: TFileId, filePath: Nullable<string>) => {
			if (fileId) {
				return fileId;
			} else if (fileId === null && filePath === null) {
				return null;
			}
			return '';
		};

		const updatedDocumentSettingsForUpdate = state.documentSettingsForUpdate.map<IDocumentSettingsForUpdate>(
			(setting) => ({
				...setting,
				fileId: finalFileId(setting.fileId, setting.filePath),
				allergensShowCondition:
					setting.filePath === null && setting.fileId === null ? null : setting.allergensShowCondition,
				termsShowInModal:
					setting.filePath === null && setting.fileId === null ? null : setting.termsShowInModal,
			}),
		);
		if (IDSelectedCustomer && restaurantID) {
			try {
				showLoader();
				await updateRestaurantStyles(IDSelectedCustomer, restaurantID, {
					ownerId: restaurantID,
					styleSettings: state.styleSettings,
					mediaSettings: updatedMediaSettingsForUpdate,
					documentSettings: updatedDocumentSettingsForUpdate,
					useCustomerSettings: state.useCustomerSettings,
					additionalSetting: {
						id: state.additionalSetting.id,
						key: state.additionalSetting.key,
						fileId: state.fileIdForAdditionalSetting,
					},
				});
				changeTypeOfModalWindow('success');
			} catch (err) {
				changeTypeOfModalWindow('error');
			} finally {
				hideLoader();
			}
		}
	}, [
		state.mediaSettingsForUpdate,
		state.documentSettingsForUpdate,
		state.styleSettings,
		state.useCustomerSettings,
		state.additionalSetting,
		state.fileIdForAdditionalSetting,
		IDSelectedCustomer,
		restaurantID,
		showLoader,
		hideLoader,
		changeTypeOfModalWindow,
	]);

	const contextValue = useMemo(
		() => ({
			refetchData,
			setStyleSettings,
			setMediaSettingsFromNormalDisplay,
			addMediaSettingsToCarousel,
			deleteMediaSettingsToCarousel,
			replacePdfToCarousel,
			toggleUseCustomerSettings,
			setFileIdForAdditionalSetting,
			setGroup,
			setLanguage,
			addGroupForLoader,
			removeGroupForLoader,
			addFileIdForDocumentSettingsForUpdate,
			setAllergenOptions,
			toggleShowModal,
			submit,
			changeTypeOfModalWindow,
			loading,
			needRefetchData: state.needRefetchData,
			styleSettings: state.styleSettings,
			styleSettingColors: state.styleSettingColors,
			mediaSettingsForUpdate: state.mediaSettingsForUpdate,
			documentSettingsForUpdate: state.documentSettingsForUpdate,
			useCustomerSettings: state.useCustomerSettings,
			additionalSetting: state.additionalSetting,
			fileIdForAdditionalSetting: state.fileIdForAdditionalSetting,
			selectedGroup: state.selectedGroup,
			selectedLanguage: state.selectedLanguage,
			groupsForLoader: state.groupsForLoader,
			typeOfModalWindow: state.typeOfModalWindow,
		}),
		[
			refetchData,
			setStyleSettings,
			setMediaSettingsFromNormalDisplay,
			addMediaSettingsToCarousel,
			deleteMediaSettingsToCarousel,
			replacePdfToCarousel,
			toggleUseCustomerSettings,
			setFileIdForAdditionalSetting,
			setGroup,
			setLanguage,
			addGroupForLoader,
			removeGroupForLoader,
			addFileIdForDocumentSettingsForUpdate,
			setAllergenOptions,
			toggleShowModal,
			submit,
			changeTypeOfModalWindow,
			loading,
			state.needRefetchData,
			state.styleSettings,
			state.styleSettingColors,
			state.mediaSettingsForUpdate,
			state.documentSettingsForUpdate,
			state.useCustomerSettings,
			state.additionalSetting,
			state.fileIdForAdditionalSetting,
			state.selectedGroup,
			state.selectedLanguage,
			state.groupsForLoader,
			state.typeOfModalWindow,
		],
	);

	return <StyleContext.Provider value={contextValue}>{children}</StyleContext.Provider>;
};

export default StyleState;
